import React, { useState, useEffect, useMemo } from 'react';
import OrganizationRequired, { useOrganizationRequired } from '../components/OrganizationRequired';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building,
  CloudUpload,
  Network,
  Trash2,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Landmark,
  Receipt,
  Loader2,
  Search,
  X,
  Filter,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';
import { apiService } from '../services/api.service';
import StripeConnectSettings from '../components/StripeConnectSettings';
import OrganizationSettings from '../components/OrganizationSettings';
import VatRegimeSettings from '../components/VatRegimeSettings';

// L'onglet « Régime TVA » n'est PAS gaté par REACT_APP_BILLING_ENABLED, et
// c'est délibéré : déclarer son régime n'émet aucun document et ne bloque
// personne. Le gater empêchait de collecter les régimes avant d'activer le
// module — alors que cette collecte est justement le préalable à l'activation.
// Le flag reprendra du service pour l'onglet « Facturation » (documents émis).

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  events_remaining: number;
  max_events: number;
  created_at: string;
}

interface SyncConfig {
  cloud_url: string;
  cloud_token?: string;
  auto_sync: boolean;
  sync_interval: number;
  last_sync?: string;
  sync_status?: 'connected' | 'disconnected' | 'error';
}


export default function Settings() {
  const { t } = useTranslation();
  const besoinOrganisation = useOrganizationRequired();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('organization');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Organisation state
  const [organization, setOrganization] = useState<Organization | null>(null);

  // Sync state - monitoring des API locales
  const [localApis, setLocalApis] = useState<any[]>([]);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  // Filtres sync history (admin only)
  const [allOrganizations, setAllOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [filterOrgId, setFilterOrgId] = useState<string>('');
  const [filterEventName, setFilterEventName] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Danger zone
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Gestion retour Stripe onboarding
  useEffect(() => {
    const stripeOnboarding = searchParams.get('stripe_onboarding');
    const tab = searchParams.get('tab');

    if (stripeOnboarding === 'success') {
      setActiveTab('stripe');
      setSuccess(t('settings.messages.stripeSuccess'));
      setTimeout(() => setSuccess(null), 5000);
      searchParams.delete('stripe_onboarding');
      searchParams.delete('tab');
      setSearchParams(searchParams);
    } else if (stripeOnboarding === 'refresh') {
      setActiveTab('stripe');
      setError(t('settings.messages.stripeRefresh'));
      searchParams.delete('stripe_onboarding');
      searchParams.delete('tab');
      setSearchParams(searchParams);
    } else if (tab === 'stripe') {
      setActiveTab('stripe');
      searchParams.delete('tab');
      setSearchParams(searchParams);
    } else if (tab === 'vat') {
      // Cible de la bannière de collecte du régime TVA.
      setActiveTab('vat');
      searchParams.delete('tab');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Charger les infos utilisateur
      const userInfo = await apiService.getCurrentUser();
      const userIsAdmin = userInfo?.role === 'admin';
      setIsAdmin(userIsAdmin);

      if (userInfo?.organization) {
        setOrganization({
          id: userInfo.organization.id || '',
          name: userInfo.organization.name,
          slug: userInfo.organization.slug,
          plan: userInfo.organization.plan,
          events_remaining: userInfo.organization.events_remaining,
          max_events: userInfo.organization.max_events || 10,
          created_at: userInfo.organization.created_at || new Date().toISOString(),
        });
      }


      // Charger les données de monitoring
      try {
        const promises: Promise<any>[] = [
          apiService.getLocalApis(),
          apiService.getSyncHistory()
        ];

        // Si admin, charger aussi la liste des organisations
        if (userIsAdmin) {
          promises.push(apiService.getAllOrganizations());
        }

        const results = await Promise.all(promises);
        setLocalApis(results[0] || []);
        setSyncHistory(results[1] || []);
        if (userIsAdmin && results[2]) {
          setAllOrganizations(results[2] || []);
        }
      } catch (err) {
        console.log('Erreur chargement données sync:', err);
      }
    } catch (err: any) {
      setError(t('settings.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setRefreshingStatus(true);
      setError(null);

      const filters: { organizationId?: string; eventName?: string } = {};
      if (filterOrgId) filters.organizationId = filterOrgId;
      if (filterEventName) filters.eventName = filterEventName;

      const [localApisData, syncHistoryData] = await Promise.all([
        apiService.getLocalApis(),
        apiService.getSyncHistory(filters)
      ]);

      setLocalApis(localApisData || []);
      setSyncHistory(syncHistoryData || []);
      setSuccess(t('settings.messages.statusUpdated'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(t('settings.messages.statusUpdateError'));
    } finally {
      setRefreshingStatus(false);
    }
  };

  // Extraire les noms d'événements uniques pour les suggestions
  const uniqueEventNames = useMemo(() => {
    const names = new Set<string>();
    syncHistory.forEach((sync) => {
      if (sync.event_name) names.add(sync.event_name);
    });
    return Array.from(names).sort();
  }, [syncHistory]);

  // Filtrer les suggestions d'événements
  const eventSuggestions = useMemo(() => {
    if (!filterEventName) return [];
    const lower = filterEventName.toLowerCase();
    return uniqueEventNames.filter((name) =>
      name.toLowerCase().includes(lower)
    ).slice(0, 5);
  }, [filterEventName, uniqueEventNames]);

  const handleDeleteAllData = async () => {
    if (deleteConfirmation !== organization?.name) {
      setError(t('settings.messages.orgNameMismatch'));
      return;
    }

    try {
      setSaving(true);
      await apiService.deleteAllData();

      // Déconnexion et redirection
      apiService.logout();
      window.location.href = '/login';
    } catch (err: any) {
      setError(t('settings.messages.deleteError'));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <p className="text-sm text-muted-foreground">{t('settings.title')}</p>

      {/* Un admin doit dire SUR QUELLE organisation il agit : sans ce garde,
          la page retomberait sur celle de son propre compte. On REMPLACE le
          contenu au lieu de l'encadrer — un formulaire prérempli avec les
          valeurs d'une autre organisation invite à enregistrer au mauvais
          endroit. */}
      {besoinOrganisation && <OrganizationRequired />}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-sm underline ml-4">
              {t('common.close')}
            </button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            {success}
            <button onClick={() => setSuccess(null)} className="text-sm underline ml-4">
              {t('common.close')}
            </button>
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border bg-card">
        {/* Sans organisation de travail, seul « Profil & Sécurité » a un sens :
            c'est le compte de la personne, pas les réglages d'une organisation. */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b">
            <TabsList className="w-full justify-start rounded-none bg-transparent p-0">
              {!besoinOrganisation && (
              <TabsTrigger
                value="organization"
                className="flex-1 md:flex-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 md:px-4 py-3 text-xs md:text-sm gap-1 md:gap-2"
              >
                <Building className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">{t('settings.tabs.organization')}</span>
              </TabsTrigger>
              )}
              {!besoinOrganisation && (
              <TabsTrigger
                value="vat"
                className="flex-1 md:flex-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 md:px-4 py-3 text-xs md:text-sm gap-1 md:gap-2"
              >
                <Receipt className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">{t('settings.tabs.vat')}</span>
              </TabsTrigger>
              )}
              {!besoinOrganisation && (
              <TabsTrigger
                value="stripe"
                className="flex-1 md:flex-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 md:px-4 py-3 text-xs md:text-sm gap-1 md:gap-2"
              >
                <Landmark className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">Stripe</span>
              </TabsTrigger>
              )}
              {!besoinOrganisation && (
              <TabsTrigger
                value="sync"
                className="flex-1 md:flex-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 md:px-4 py-3 text-xs md:text-sm gap-1 md:gap-2"
              >
                <CloudUpload className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">{t('settings.tabs.sync')}</span>
              </TabsTrigger>
              )}
              {!besoinOrganisation && (
              <TabsTrigger
                value="danger"
                className="flex-1 md:flex-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 md:px-4 py-3 text-xs md:text-sm gap-1 md:gap-2"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">{t('settings.tabs.danger')}</span>
              </TabsTrigger>
              )}
            </TabsList>
          </div>

          <div className="p-4 md:p-6">
            {!besoinOrganisation && (
            <TabsContent value="organization" className="mt-0">
              <OrganizationSettings
                onSuccess={(msg) => {
                  setSuccess(msg);
                  setTimeout(() => setSuccess(null), 3000);
                }}
                onError={(msg) => setError(msg)}
              />
            </TabsContent>
            )}

            {!besoinOrganisation && (
            <TabsContent value="vat" className="mt-0">
              <VatRegimeSettings
                onSuccess={(msg) => {
                  setSuccess(msg);
                  setTimeout(() => setSuccess(null), 3000);
                }}
                onError={(msg) => setError(msg)}
              />
            </TabsContent>
            )}

            {!besoinOrganisation && (
            <TabsContent value="stripe" className="mt-0">
              <StripeConnectSettings />
            </TabsContent>
            )}

            {!besoinOrganisation && (
            <TabsContent value="sync" className="mt-0">
              <div className="space-y-6">
                {/* API Locales Connectées */}
                <Card>
                  <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <CardTitle className="text-base md:text-lg">{t('settings.localApis.title')}</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshStatus}
                      disabled={refreshingStatus}
                      className="w-full sm:w-auto"
                    >
                      <Network className="h-4 w-4 mr-2" />
                      {refreshingStatus ? t('settings.localApis.refreshing') : t('settings.localApis.refresh')}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {localApis.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {t('settings.localApis.noApis')}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {localApis.map((api, index) => {
                          const syncSourceMatch = api.sync_source?.match(/^([^(]+?)(?:\s*\(([^)]+)\))?$/);
                          const apiAddress = syncSourceMatch ? syncSourceMatch[1].trim() : api.sync_source;
                          const apiName = syncSourceMatch && syncSourceMatch[2] ? syncSourceMatch[2].trim() : null;

                          return (
                            <div
                              key={index}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 md:p-4 border rounded-lg"
                            >
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate">{apiName || apiAddress}</p>
                                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                                  {apiAddress} • {t('settings.localApis.lastSync')}: {api.last_sync ? new Date(api.last_sync).toLocaleString() : t('settings.localApis.never')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {api.sync_count > 1 ? t('settings.localApis.syncCountPlural', { count: api.sync_count }) : t('settings.localApis.syncCount', { count: api.sync_count })}
                                </p>
                              </div>
                              <Badge
                                variant={api.status === 'online' ? 'default' : api.status === 'idle' ? 'secondary' : 'destructive'}
                                className={cn(
                                  "flex items-center gap-1",
                                  api.status === 'online' && "bg-success/20 text-success border-success/50",
                                  api.status === 'idle' && "bg-warning/20 text-warning border-warning/50"
                                )}
                              >
                                {api.status === 'online' ? (
                                  <CheckCircle className="h-3 w-3" />
                                ) : api.status === 'idle' ? (
                                  <AlertTriangle className="h-3 w-3" />
                                ) : (
                                  <AlertCircle className="h-3 w-3" />
                                )}
                                {api.status === 'online' ? t('settings.localApis.statusOnline') : api.status === 'idle' ? t('settings.localApis.statusIdle') : t('settings.localApis.statusOffline')}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Historique des synchronisations */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">{t('settings.syncHistory.title')}</CardTitle>
                    {(filterOrgId || filterEventName) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFilterOrgId('');
                          setFilterEventName('');
                          handleRefreshStatus();
                        }}
                        className="text-muted-foreground"
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('settings.syncHistory.reset')}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {/* Filtres */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      {/* Filtre organisation (admin uniquement) */}
                      {isAdmin && (
                        <div className="flex-1 min-w-[200px]">
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            {t('settings.syncHistory.organization')}
                          </Label>
                          <Select value={filterOrgId} onValueChange={(value) => {
                            setFilterOrgId(value === 'all' ? '' : value);
                          }}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={t('settings.syncHistory.allOrganizations')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{t('settings.syncHistory.allOrganizations')}</SelectItem>
                              {allOrganizations.map((org) => (
                                <SelectItem key={org.id} value={org.id}>
                                  {org.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Filtre événement avec suggestions */}
                      <div className="flex-1 min-w-[200px] relative">
                        <Label className="text-xs text-muted-foreground mb-1 block">
                          {t('settings.syncHistory.event')}
                        </Label>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder={t('settings.syncHistory.searchEvent')}
                            value={filterEventName}
                            onChange={(e) => setFilterEventName(e.target.value)}
                            className="h-9 pl-8"
                          />
                          {filterEventName && (
                            <button
                              onClick={() => setFilterEventName('')}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {/* Suggestions dropdown */}
                        {eventSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md">
                            {eventSuggestions.map((name, idx) => (
                              <button
                                key={idx}
                                onClick={() => {
                                  setFilterEventName(name);
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-white/[0.06] first:rounded-t-md last:rounded-b-md"
                              >
                                {name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Bouton appliquer */}
                      <div className="flex items-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRefreshStatus}
                          disabled={refreshingStatus}
                          className="h-9"
                        >
                          <Filter className="h-4 w-4 mr-1.5" />
                          {refreshingStatus ? t('settings.syncHistory.filtering') : t('settings.syncHistory.filter')}
                        </Button>
                      </div>
                    </div>

                    {syncHistory.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {t('settings.syncHistory.noSync')}
                      </p>
                    ) : (
                      <div className="max-h-[300px] overflow-auto space-y-0">
                        {syncHistory.slice(0, 15).map((sync, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-3 border-b last:border-b-0"
                          >
                            <div className="flex items-start gap-3">
                              {/* Icône direction */}
                              <div className={cn(
                                "mt-0.5 text-lg",
                                "text-muted-foreground"
                              )}>
                                {sync.direction === 'download' ? '⬇️' : '⬆️'}
                              </div>
                              <div>
                                <p className="text-sm">
                                  <strong>{sync.event_name || t('settings.syncHistory.unknownEvent')}</strong>
                                  {isAdmin && sync.organization_name && (
                                    <span className="ml-2 text-xs text-foreground">
                                      ({sync.organization_name})
                                    </span>
                                  )}
                                  {sync.entity_count > 0 && (
                                    <span className="ml-2 text-muted-foreground">
                                      • {sync.entity_count} {sync.entity_type || t('settings.syncHistory.elements')}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {sync.sync_source} • {sync.sync_timestamp ? new Date(sync.sync_timestamp).toLocaleString() : t('settings.syncHistory.unknownDate')}
                                </p>
                              </div>
                            </div>
                            <Badge
                              variant={sync.status === 'success' ? 'default' : 'destructive'}
                              className={cn(
                                "flex items-center gap-1 shrink-0",
                                sync.status === 'success' && "bg-success/20 text-success border-success/50"
                              )}
                            >
                              {sync.status === 'success' ? (
                                <CheckCircle className="h-3 w-3" />
                              ) : (
                                <AlertCircle className="h-3 w-3" />
                              )}
                              {sync.status === 'success' ? t('settings.syncHistory.statusOk') : t('settings.syncHistory.statusFailed')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            )}


            {!besoinOrganisation && (
            <TabsContent value="danger" className="mt-0">
              <Card className="border-2 border-destructive">
                <CardHeader className="bg-destructive/10">
                  <CardTitle className="text-lg text-destructive">{t('settings.dangerZone.title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-destructive mb-2">
                    {t('settings.dangerZone.deleteAllData')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('settings.dangerZone.deleteWarning')}
                  </p>
                  <Button
                    variant="outline"
                    className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('settings.dangerZone.deleteButton')}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            )}
          </div>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {t('settings.dangerZone.confirmTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.dangerZone.confirmDescription')}
              <strong> {organization?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              autoFocus
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={t('settings.dangerZone.typeName')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAllData}
              disabled={deleteConfirmation !== organization?.name || saving}
            >
              {t('settings.dangerZone.deleteForever')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}